<template>
  <div class="kanban-filter-container">
    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_KANBAN.FILTERS.PIPELINE') }}</label>
      <multiselect
        v-model="selectedPipeline"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_KANBAN.FILTERS.SELECT_PIPELINE')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="pipelines"
        :allow-empty="true"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group">
      <label>{{ $t('OPPORTUNITY_KANBAN.FILTERS.SEARCH') }}</label>
      <input
        v-model.trim="filters.search"
        type="text"
        :disabled="disabled"
        :placeholder="$t('OPPORTUNITY_KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
      />
    </div>

    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_KANBAN.FILTERS.STATUS') }}</label>
      <multiselect
        v-model="selectedStatus"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_KANBAN.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="statusOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_KANBAN.FILTERS.SLA_STATUS') }}</label>
      <multiselect
        v-model="selectedSlaStatus"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_KANBAN.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="slaStatusOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div v-if="showOwnerFilter" class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_KANBAN.FILTERS.OWNER') }}</label>
      <multiselect
        v-model="selectedOwner"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_KANBAN.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="ownerOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_KANBAN.FILTERS.INBOX') }}</label>
      <multiselect
        v-model="selectedInbox"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_KANBAN.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="inboxOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group filter-group--buttons">
      <label class="filter-group__spacer" aria-hidden="true" />
      <div class="button-wrapper">
        <woot-button :is-disabled="disabled" @click="applyFilters">
          {{ $t('OPPORTUNITY_KANBAN.FILTERS.APPLY') }}
        </woot-button>
        <woot-button
          color-scheme="secondary"
          :is-disabled="disabled"
          @click="clearFilters"
        >
          {{ $t('OPPORTUNITY_KANBAN.FILTERS.CLEAR') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_KANBAN_FILTERS as DEFAULT_FILTERS } from 'dashboard/helper/opportunityKanbanHelper';
import {
  withAllOption,
  findFilterOption,
  translatedFilterOptions,
} from 'dashboard/helper/opportunityFilterHelper';

const STATUSES = ['open', 'won', 'lost', 'canceled'];
const SLA_STATUSES = ['on_time', 'warning', 'overdue'];

export default {
  props: {
    pipelines: { type: Array, default: () => [] },
    agents: { type: Array, default: () => [] },
    inboxes: { type: Array, default: () => [] },
    initialFilters: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    showOwnerFilter: { type: Boolean, default: true },
  },
  data() {
    return {
      filters: { ...DEFAULT_FILTERS(), ...this.initialFilters },
    };
  },
  computed: {
    allLabel() {
      return this.$t('OPPORTUNITY_KANBAN.FILTERS.ALL');
    },
    statusOptions() {
      return withAllOption(
        this.allLabel,
        translatedFilterOptions(STATUSES, this.statusOptionLabel)
      );
    },
    slaStatusOptions() {
      return withAllOption(
        this.allLabel,
        translatedFilterOptions(SLA_STATUSES, this.slaStatusOptionLabel)
      );
    },
    ownerOptions() {
      return withAllOption(this.allLabel, this.agents);
    },
    inboxOptions() {
      return withAllOption(this.allLabel, this.inboxes);
    },
    selectedStatus: {
      get() {
        return findFilterOption(this.statusOptions, this.filters.status);
      },
      set(option) {
        this.filters.status = option ? option.id : null;
      },
    },
    selectedSlaStatus: {
      get() {
        return findFilterOption(this.slaStatusOptions, this.filters.slaStatus);
      },
      set(option) {
        this.filters.slaStatus = option ? option.id : null;
      },
    },
    selectedPipeline: {
      get() {
        return (
          this.pipelines.find(
            pipeline => pipeline.id === this.filters.pipelineId
          ) || null
        );
      },
      set(pipeline) {
        this.filters.pipelineId = pipeline ? pipeline.id : null;
      },
    },
    selectedOwner: {
      get() {
        return findFilterOption(this.ownerOptions, this.filters.ownerId);
      },
      set(agent) {
        this.filters.ownerId = agent ? agent.id : null;
      },
    },
    selectedInbox: {
      get() {
        return findFilterOption(this.inboxOptions, this.filters.inboxId);
      },
      set(inbox) {
        this.filters.inboxId = inbox ? inbox.id : null;
      },
    },
  },
  watch: {
    'initialFilters.pipelineId': function watchInitialPipelineId(pipelineId) {
      if (pipelineId && !this.filters.pipelineId) {
        this.filters.pipelineId = pipelineId;
      }
    },
  },
  methods: {
    statusOptionLabel(status) {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.STATUS_VALUES.${status}`
      );
    },
    slaStatusOptionLabel(slaStatus) {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.SLA_VALUES.${slaStatus}`
      );
    },
    applyFilters() {
      this.$emit('filter-applied', {
        ...this.filters,
        ownerId: this.showOwnerFilter ? this.filters.ownerId : null,
      });
    },
    clearFilters() {
      this.filters = {
        ...DEFAULT_FILTERS(),
        pipelineId: this.filters.pipelineId,
      };
      this.applyFilters();
    },
  },
};
</script>

<style scoped lang="scss">
.kanban-filter-container {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 12rem;

  label {
    font-size: var(--font-size-mini);
    margin-bottom: var(--space-micro);
    line-height: 1.6rem;
  }

  select,
  input {
    height: 4rem;
    margin-bottom: 0;
  }
}

.filter-group--buttons {
  min-width: 0;
}

.filter-group__spacer {
  display: block;
  height: 1.6rem;
  margin-bottom: var(--space-micro);
  visibility: hidden;
}

.button-wrapper {
  display: flex;
  gap: var(--space-smaller);
  height: 4rem;
}
</style>
